import type { ReactNode } from 'react'
import { cn } from '../../lib/cn'

export function Card({ title, subtitle, actions, children, className, flush = false }: {
  title?: ReactNode; subtitle?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string
  /** No body padding (tables, lists that run edge to edge). */
  flush?: boolean
}) {
  return (
    <section className={cn('rounded-xl border border-line bg-cream-50', flush && 'card-flush', className)}>
      {(title || actions) && (
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-3.5">
          <div className="min-w-0">
            {title && <h2 className="text-base font-semibold text-ink-100">{title}</h2>}
            {subtitle && <p className="mt-0.5 text-[12.5px] text-ink-400">{subtitle}</p>}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={flush ? '' : 'p-5'}>{children}</div>
    </section>
  )
}

export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        <h1 className="text-2xl font-semibold leading-8 tracking-tight text-ink-100">{title}</h1>
        {subtitle && <p className="mt-1 text-[13px] text-ink-300">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  )
}

export function EmptyState({ title, body, action, icon }: { title: ReactNode; body?: ReactNode; action?: ReactNode; icon?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-cream-300 px-6 py-10 text-center">
      {icon && <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-cream-200 text-ink-300">{icon}</div>}
      <p className="text-sm font-semibold text-ink-100">{title}</p>
      {body && <p className="mt-1 max-w-sm text-[13px] text-ink-400">{body}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

export type BadgeTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger'

export function Badge({ children, tone = 'neutral', className }: { children: ReactNode; tone?: BadgeTone; className?: string }) {
  const tones: Record<BadgeTone, string> = {
    neutral: 'bg-cream-200 text-ink-400',
    accent: 'bg-ginger-100 text-ginger-600',
    success: 'bg-mint-50 text-mint-500',
    warning: 'bg-amber-50 text-amber-700',
    danger: 'bg-red-50 text-red-600'
  }
  return <span className={cn('inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11.5px] font-medium leading-normal', tones[tone], className)}>{children}</span>
}

/** One label/value line in a `dl`: the summary rail in the editor, the run's metadata aside. */
export function InfoRow({ label, value }: { label: ReactNode; value: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="shrink-0 text-[12.5px] text-ink-500">{label}</dt>
      <dd className="min-w-0 truncate text-right text-[12.5px] font-medium text-ink-100">{value}</dd>
    </div>
  )
}

/** Stat tile (number + caption), e.g. on Automations and Usage. */
export function Stat({ label, value, note, tone }: { label: ReactNode; value: ReactNode; note?: ReactNode; tone?: 'warning' }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-xl border border-line bg-cream-50 p-4">
      <div className="text-[13px] text-ink-300">{label}</div>
      <div className={cn('text-2xl font-semibold leading-8 text-ink-100 tabular-nums', tone === 'warning' && 'text-amber-700')}>{value}</div>
      {note && <div className="text-xs text-ink-400">{note}</div>}
    </div>
  )
}
